import cn from 'classnames';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { isTerminalStatus } from '@/components/Agents/AgentSessionPage/Activity/ActivityRow.utils';
import { statusBadge } from '@/components/Agents/AgentSessionPage/AgentTree.utils';
import { agentDisplayMap, isAgentEnabled } from '@/components/Agents/AgentUtils';
import Button from '@/components/UI/Button/Button';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import FindingChips from '@/components/UI/FindingChips/FindingChips';
import NodeBadge from '@/components/UI/GraphCanvas/nodes/NodeBadge';
import Link from '@/components/UI/Link';
import Spinner from '@/components/UI/Spinner/Spinner';
import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import {
  type AgentFindingCountsV1,
  type AgentSessionApiId,
  type AgentSessionStatus,
  type AgentTreeNodeApiId,
} from '@/model/Agent.model';
import type { ISODateTime } from '@/model/Common.model';
import type { StudyApiId } from '@/model/Study.model';
import { AgentSessionStreamService } from '@/support/agentStream/AgentSessionStreamService';
import { type SingleFlightPass, useSingleFlight } from '@/support/agentStream/useSingleFlight';
import { watchSessionStream } from '@/support/agentStream/watchSessionStream';
import { ResponseType } from '@/support/ApiService';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { web as webRoute } from '@/support/route';
import { featuresSelector } from '@/support/Selectors';
import { DateUtils } from '@/utils/Date.utils';
import { useModalAction } from '@/utils/modal';

import styles from './HealthCheckCard.module.scss';

interface HealthCheckCardProps {
  studyApiId: StudyApiId;
}

interface HealthCheckRun {
  apiId: AgentSessionApiId;
  createdAt: ISODateTime;
  status: AgentSessionStatus | 'queued';
  findings?: AgentFindingCountsV1 | null;
  reportNodeApiId?: AgentTreeNodeApiId | null;
}

// perPage only accepts the PerPageQueryParameter sizes, so the card requests the smallest and slices
// to RECENT_RUN_LIMIT, re-slicing after an optimistic queued row is merged in.
const HISTORY_PAGE_SIZE = 5;
const RECENT_RUN_LIMIT = 2;

/** Labels for the statuses the hub's badge does not name; every other status uses the badge tooltip. */
const STATUS_LABEL: Partial<Record<HealthCheckRun['status'], string>> = {
  queued: 'Queued',
  awaiting_input: 'Awaiting input',
};

/**
 * The run's status with the same icon and wording the agents hub shows on the session graph. A queued
 * run gets the running icon, since it is already underway from the user's point of view. The hub has
 * no icon for `awaiting_input`, so that status is shown as its label alone.
 */
const RunStatus: FC<{ status: HealthCheckRun['status'] }> = ({ status }) => {
  const badge = statusBadge(status === 'queued' ? 'running' : status);

  return (
    <span
      className={styles.Status}
      data-test-component="HealthCheckCard"
      data-test-element="status"
      data-test-key={status}
    >
      {_notNil(badge) && <NodeBadge badge={badge} className={styles.StatusIcon} decorative />}
      {STATUS_LABEL[status] ?? badge?.tooltip}
    </span>
  );
};

/** A run's outcome: its finding counts once it has completed with a report, otherwise its status. */
const Findings: FC<{ run: HealthCheckRun; highlighted: boolean }> = ({ run, highlighted }) => {
  if (run.status !== 'completed' || _isNil(run.findings)) {
    return <RunStatus status={run.status} />;
  }

  return <FindingChips counts={run.findings} muted={!highlighted} className={styles.Chips} />;
};

const byCreatedAtDesc = (left: HealthCheckRun, right: HealthCheckRun): number =>
  Date.parse(right.createdAt) - Date.parse(left.createdAt);

const isInFlight = (run: HealthCheckRun): boolean => run.status === 'queued' || !isTerminalStatus(run.status);

/**
 * The study overview's entry point to the study health-check agent: starts a run, shows the most
 * recent runs with their findings, and opens a run's report. Rows for runs still in progress update
 * live as each run finishes. Renders nothing unless the health-check agent is enabled for the team.
 */
const HealthCheckCard: FC<HealthCheckCardProps> = ({ studyApiId }) => {
  const features = useSelector(featuresSelector);
  const healthCheckEnabled = isAgentEnabled(features, 'study_health_check');
  const { openModal } = useModalAction();
  const [startedRuns, setStartedRuns] = useState<Array<HealthCheckRun>>([]);
  const [historyRuns, setHistoryRuns] = useState<Array<HealthCheckRun>>();
  const [historyFailed, setHistoryFailed] = useState(false);

  const { loading: historyLoading, invoke: loadHistory } = useApiHook({
    endpoint: 'GET /api/v1/ai-agents/study-health-check/studies/{studyApiId}/sessions',
    path: { studyApiId },
    query: { perPage: HISTORY_PAGE_SIZE },
    options: {
      invokeOnInit: false,
      onError: {
        toast: false,
        throw: false,
        capture: true,
        slug: 'study-overview-health-checks-load',
      },
    },
  });

  const { loading, invoke: createSession } = useApiHook({
    endpoint: 'POST /api/v1/ai-agents/study-health-check',
    options: {
      invokeOnInit: false,
      onError: {
        slug: 'study-overview-health-check-create',
        toast_message: 'Failed to start the study health check. Please try again.',
        throw: false,
      },
    },
  });

  const loadHistoryRef = useRef(loadHistory);
  useEffect(() => {
    loadHistoryRef.current = loadHistory;
  });

  // Single-flight, so a trigger that arrives mid-fetch (a stream event, a kickoff) queues one more
  // fetch rather than settling on a snapshot taken before it. A failed fetch keeps the last good list
  // and retries, since the terminal event that triggered it is not delivered again; the error state is
  // only for a card that never loaded, where Retry is the user's call.
  const hasLoadedRef = useRef(false);
  const refreshPass = useCallback(async ({ retry }: SingleFlightPass): Promise<void> => {
    const response = await loadHistoryRef.current().catch(() => undefined);
    if (response?.type === ResponseType.Success) {
      setHistoryRuns(
        response.body.data.map((run) => ({
          apiId: run.api_id,
          createdAt: run.created_at,
          status: run.status,
          findings: run.findings ?? null,
          reportNodeApiId: run.report_node_api_id ?? null,
        }))
      );
      hasLoadedRef.current = true;
      setHistoryFailed(false);
    } else {
      setHistoryFailed(true);
      if (hasLoadedRef.current) {
        await retry();
      }
    }
  }, []);
  const refreshHistory = useSingleFlight(refreshPass);

  useEffect(() => {
    if (healthCheckEnabled) {
      void refreshHistory();
    }
  }, [healthCheckEnabled, refreshHistory]);

  const runHealthCheck = useCallback(async (): Promise<void> => {
    const response = await createSession({ body: { study_api_id: studyApiId } });

    if (response.type === ResponseType.Success) {
      setStartedRuns((currentRuns) => [
        {
          apiId: response.body.api_id,
          createdAt: response.body.created_at,
          status: 'queued',
        },
        ...currentRuns,
      ]);
      await refreshHistory();
    }
  }, [createSession, refreshHistory, studyApiId]);

  const openReport = useCallback(
    (run: HealthCheckRun): void => {
      if (_isNil(run.reportNodeApiId)) {
        return;
      }

      openModal('AGENT_REPORT', {
        sessionApiId: run.apiId,
        nodeApiId: run.reportNodeApiId,
        agentName: agentDisplayMap.study_health_check.name,
      });
    },
    [openModal]
  );

  const { visibleRuns, activeSessionKey } = useMemo(() => {
    const loadedRuns = historyRuns ?? [];
    const loadedRunIds = new Set(loadedRuns.map((run) => run.apiId));
    const runs = [...startedRuns.filter((run) => !loadedRunIds.has(run.apiId)), ...loadedRuns]
      .sort(byCreatedAtDesc)
      .slice(0, RECENT_RUN_LIMIT);
    const inFlightKey = runs
      .reduce<Array<AgentSessionApiId>>((sessionApiIds, run) => {
        if (isInFlight(run)) {
          sessionApiIds.push(run.apiId);
        }
        return sessionApiIds;
      }, [])
      .sort()
      .join(',');
    return { visibleRuns: runs, activeSessionKey: inFlightKey };
  }, [startedRuns, historyRuns]);
  const showHistoryError = historyFailed && _isNil(historyRuns);
  const isInitialHistoryLoad = _isNil(historyRuns) && !historyFailed;

  // Keyed on the joined ids so a refresh that returns the same in-flight runs keeps their streams open.
  useEffect(() => {
    if (!healthCheckEnabled || _isEmpty(activeSessionKey)) {
      return;
    }

    const stops = activeSessionKey.split(',').map((sessionApiId) =>
      watchSessionStream(new AgentSessionStreamService(sessionApiId as AgentSessionApiId), {
        onTerminal: () => void refreshHistory(),
        onDrop: () => void refreshHistory(),
      })
    );

    return () => {
      stops.forEach((stop) => {
        stop();
      });
    };
  }, [healthCheckEnabled, activeSessionKey, refreshHistory]);

  if (!healthCheckEnabled) {
    return null;
  }

  return (
    <section
      className="ui-card pa3 mb4"
      aria-labelledby="health-check-card-title"
      aria-busy={isInitialHistoryLoad}
      data-test-component="HealthCheckCard"
      data-testid="overview-health-check"
    >
      <div className={styles.Header} data-test-component="HealthCheckCard" data-test-element="header">
        <h2 id="health-check-card-title" className="f5 basier-med lh-title near-black ma0 inline-flex items-center">
          Health Check
          <span className={styles.BetaPill}>Beta</span>
        </h2>
        {_isNotEmpty(visibleRuns) && (
          <div className={styles.HeaderActions}>
            <Link
              className="link blue"
              to={webRoute('agents', undefined, { tab: 'sessions' })}
              data-testid="view-all-health-check-sessions"
            >
              View all sessions
            </Link>
            <Button
              primary
              onClick={runHealthCheck}
              loading={loading}
              loadingText="Starting"
              testId="run-health-check-again"
            >
              Run again
            </Button>
          </div>
        )}
      </div>

      {isInitialHistoryLoad ? (
        <div
          className={cn(styles.EmptyState, 'pa4 tc')}
          data-test-component="HealthCheckCard"
          data-test-element="loading-state"
          role="status"
        >
          <Spinner size="large" color="blue" className="dib" />
          <span className="clip">Loading health checks</span>
        </div>
      ) : showHistoryError ? (
        <div
          className={cn(styles.EmptyState, 'pa4 tc')}
          data-test-component="HealthCheckCard"
          data-test-element="error-state"
        >
          <p className="f6 lh-copy mid-gray center mt0 mb3">Unable to load health checks.</p>
          <Button outline onClick={() => void refreshHistory()} loading={historyLoading} testId="retry-health-checks">
            Retry
          </Button>
        </div>
      ) : _isEmpty(visibleRuns) ? (
        <div
          className={cn(styles.EmptyState, 'pa4 tc')}
          data-test-component="HealthCheckCard"
          data-test-element="empty-state"
        >
          <h3 className="f3 normal lh-title near-black ma0">No health checks yet</h3>
          <p className="f6 lh-copy mid-gray center mt2 mb3 mw6">
            Analyze trends, detect anomalies, and surface actionable insights across measurements, observations, dosing,
            and alerts for a study.
          </p>
          <Button
            primary
            onClick={runHealthCheck}
            loading={loading}
            loadingText="Starting"
            testId="run-first-health-check"
          >
            Run first health check
          </Button>
        </div>
      ) : (
        <div className={styles.RunList} data-test-component="HealthCheckCard" data-test-element="run-list">
          {visibleRuns.map((run, index) => {
            const highlighted = index === 0;
            return (
              <div
                key={run.apiId}
                className={cn(styles.RunRow, { [styles.MostRecent]: highlighted })}
                data-test-component="HealthCheckCard"
                data-test-element="run"
                data-test-key={run.apiId}
              >
                <div className={styles.RunDate}>
                  {highlighted && <span className={styles.MostRecentLabel}>Most recent</span>}
                  <span className={highlighted ? 'f5' : undefined}>
                    <DateTimeRenderer value={run.createdAt} />
                  </span>
                </div>
                <div className={styles.RunResult}>
                  <Findings run={run} highlighted={highlighted} />
                  <span className={styles.Divider} aria-hidden="true" />
                  <button
                    type="button"
                    className={cn('link-button', styles.ReportLink)}
                    disabled={_isNil(run.reportNodeApiId)}
                    onClick={() => openReport(run)}
                    aria-label={`View report for the run on ${DateUtils.renderDateTime(run.createdAt)}`}
                    data-test-component="HealthCheckCard"
                    data-test-element="report-link"
                    data-test-key={run.apiId}
                  >
                    View report
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default HealthCheckCard;
